import React, { useState } from 'react';
import { API } from '../utils/api';
import LucideIcon from './LucideIcon';

interface ExamplePrompt {
  text: string;
  category: 'Basic' | 'Advanced' | 'Creative';
}

const EXAMPLE_PROMPTS: ExamplePrompt[] = [
  { text: 'Write a short poem about artificial intelligence', category: 'Basic' },
  { text: 'Explain quantum computing to a 10-year-old', category: 'Basic' },
  { text: 'Create a recipe for chocolate chip cookies', category: 'Basic' },
  { text: 'Generate a detailed system architecture for a real-time chat application', category: 'Advanced' },
  { text: 'Write a function that implements binary search in Python with detailed comments', category: 'Advanced' },
  { text: 'Create a storytelling prompt that combines cyberpunk and fairy tale elements', category: 'Creative' },
  { text: 'Design a unique alien species and their culture', category: 'Creative' },
];

const PRO_TIPS = [
  'Be specific and clear',
  'Provide context when needed',
  'Use role-playing techniques',
  'Break complex tasks into steps',
];

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22,
};

const sliderLabel: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6,
};

const Playground: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<'Basic' | 'Advanced' | 'Creative'>('Basic');
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({ temperature: 0.7, top_k: 40, max_tokens: 1000 });

  const handleSubmit = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError('');
    setResult('');
    try {
      const response = await API.post('/prompt', {
        prompt,
        temperature: settings.temperature,
        top_k: settings.top_k,
        max_tokens: settings.max_tokens,
      });
      setResult(response.response || '');
    } catch (e: any) {
      // A real failure is shown as a real error -- never disguised as a
      // response, which is what this used to do (a hardcoded paragraph
      // claiming to be "connected to Bedrock" no matter what actually failed).
      setError(e?.message || 'Could not generate a response. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1100 }}>
      <div className="ws-fade-up" style={panel}>
        <h1 className="ws-display" style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Playground</h1>
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>Experiment with any prompt and see the AI's response instantly.</p>
      </div>

      <div className="ws-playground-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="ws-fade-up" style={{ ...panel, animationDelay: '60ms' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 className="ws-display" style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Your prompt</h2>
              <button
                type="button"
                onClick={() => setShowSettings(!showSettings)}
                style={{
                  fontSize: 12.5, fontWeight: 600, padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${showSettings ? 'var(--ember)' : 'var(--border)'}`,
                  background: showSettings ? 'var(--ember-soft)' : 'var(--surface-sunken)',
                  color: showSettings ? 'var(--ember-strong)' : 'var(--text-secondary)',
                }}
              >
                Settings
              </button>
            </div>

            {showSettings && (
              <div className="ws-rise" style={{ marginBottom: 18, padding: 16, borderRadius: 10, background: 'var(--surface-sunken)', border: '1px solid var(--border)' }}>
                <p className="ws-mono" style={{ margin: '0 0 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                  Model parameters
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={sliderLabel}>Temperature: {settings.temperature.toFixed(2)}</label>
                    <input type="range" min="0" max="1" step="0.01" value={settings.temperature}
                      onChange={(e) => setSettings({ ...settings, temperature: parseFloat(e.target.value) })}
                      style={{ width: '100%', accentColor: 'var(--ember)' }} />
                    <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--text-tertiary)' }}>Higher = more creative, lower = more focused</p>
                  </div>
                  <div>
                    <label style={sliderLabel}>Top-K: {settings.top_k}</label>
                    <input type="range" min="1" max="100" step="1" value={settings.top_k}
                      onChange={(e) => setSettings({ ...settings, top_k: parseInt(e.target.value, 10) })}
                      style={{ width: '100%', accentColor: 'var(--ember)' }} />
                    <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--text-tertiary)' }}>Limits vocabulary selection</p>
                  </div>
                  <div>
                    <label style={sliderLabel}>Max response length: {settings.max_tokens}</label>
                    <input type="range" min="100" max="4096" step="100" value={settings.max_tokens}
                      onChange={(e) => setSettings({ ...settings, max_tokens: parseInt(e.target.value, 10) })}
                      style={{ width: '100%', accentColor: 'var(--ember)' }} />
                    <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--text-tertiary)' }}>Maximum tokens in the response</p>
                  </div>
                </div>
              </div>
            )}

            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Write your prompt here…"
              className="ws-mono"
              style={{
                width: '100%', boxSizing: 'border-box', minHeight: 160, resize: 'vertical', fontSize: 13, lineHeight: 1.7,
                padding: 16, color: 'var(--text-primary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)',
                borderRadius: 10, outline: 'none',
              }}
            />

            {error && <p role="alert" className="ws-rise" style={{ margin: '12px 0 0', fontSize: 13.5, color: 'var(--danger, #b42318)' }}>{error}</p>}

            <div style={{ marginTop: 14 }}>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !prompt.trim()}
                className="btn-live ws-display"
                style={{
                  padding: '12px 26px', borderRadius: 10, border: 'none', fontSize: 14.5, fontWeight: 700,
                  cursor: loading || !prompt.trim() ? 'not-allowed' : 'pointer',
                  background: loading || !prompt.trim() ? 'var(--surface-sunken)' : 'var(--ember)',
                  color: loading || !prompt.trim() ? 'var(--text-tertiary)' : 'white',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}
              >
                {loading ? 'Generating…' : 'Generate response'}
              </button>
            </div>
          </div>

          {result && (
            <div className="ws-rise" style={panel}>
              <h3 className="ws-display" style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Response</h3>
              <pre className="ws-mono" style={{
                margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12.5, lineHeight: 1.8,
                color: 'var(--text-primary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)',
                borderRadius: 10, padding: 16,
              }}>
                {result}
              </pre>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="ws-fade-up" style={{ ...panel, animationDelay: '100ms' }}>
            <h3 className="ws-display" style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Example prompts</h3>
            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
              {(['Basic', 'Advanced', 'Creative'] as const).map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setSelectedCategory(category)}
                  style={{
                    fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${selectedCategory === category ? 'var(--ember)' : 'var(--border)'}`,
                    background: selectedCategory === category ? 'var(--ember-soft)' : 'var(--surface-sunken)',
                    color: selectedCategory === category ? 'var(--ember-strong)' : 'var(--text-secondary)',
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {EXAMPLE_PROMPTS.filter((p) => p.category === selectedCategory).map((p) => (
                <button
                  key={p.text}
                  type="button"
                  onClick={() => setPrompt(p.text)}
                  style={{
                    textAlign: 'left', fontSize: 12.5, lineHeight: 1.5, padding: '10px 12px', borderRadius: 8,
                    border: '1px solid var(--border)', background: 'var(--surface-sunken)', color: 'var(--text-secondary)', cursor: 'pointer',
                  }}
                >
                  {p.text}
                </button>
              ))}
            </div>
          </div>

          <div className="ws-fade-up" style={{ ...panel, animationDelay: '140ms' }}>
            <h3 className="ws-display" style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Pro tips</h3>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {PRO_TIPS.map((tip) => (
                <li key={tip} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--ember-strong)', flex: '0 0 auto', marginTop: 2 }}><LucideIcon name="tip" size={14} /></span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Playground;
